/**
 * EntityFrameworkScoping — multi-select for entity and framework scoping.
 */

const FRAMEWORKS = ['US_GAAP', 'IFRS'];

interface EntityFrameworkScopingProps {
  entities: string[];
  frameworks: string[];
  availableEntities: Array<{ code: string; name: string }>;
  onEntitiesChange: (entities: string[]) => void;
  onFrameworksChange: (frameworks: string[]) => void;
}

const checkboxStyle: React.CSSProperties = {
  marginRight: 6,
  cursor: 'pointer',
  accentColor: 'var(--accent, var(--accent))',
};

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  marginRight: 14,
  color: 'var(--text-secondary)',
};

export function EntityFrameworkScoping({
  entities, frameworks, availableEntities,
  onEntitiesChange, onFrameworksChange,
}: EntityFrameworkScopingProps) {
  const allEntities = entities.length === 0;
  const allFrameworks = frameworks.length === 0;

  const toggleEntity = (code: string) => {
    if (entities.includes(code)) {
      onEntitiesChange(entities.filter(e => e !== code));
    } else {
      onEntitiesChange([...entities, code]);
    }
  };

  const toggleFramework = (fw: string) => {
    if (frameworks.includes(fw)) {
      onFrameworksChange(frameworks.filter(f => f !== fw));
    } else {
      onFrameworksChange([...frameworks, fw]);
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      {/* Entity scoping */}
      <div>
        <label style={{ fontSize: 11, color: 'var(--text-tertiary)', display: 'block', marginBottom: 6 }}>
          Applies to Entities
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <label style={labelStyle}>
            <input
              type="checkbox"
              checked={allEntities}
              onChange={() => onEntitiesChange([])}
              style={checkboxStyle}
            />
            All
          </label>
          {availableEntities.map(ent => (
            <label key={ent.code} style={labelStyle}>
              <input
                type="checkbox"
                checked={allEntities || entities.includes(ent.code)}
                onChange={() => {
                  if (allEntities) {
                    // Switching from "all" to specific: select all except this one
                    onEntitiesChange(availableEntities.filter(e => e.code !== ent.code).map(e => e.code));
                  } else {
                    toggleEntity(ent.code);
                  }
                }}
                style={checkboxStyle}
              />
              {ent.name || ent.code}
            </label>
          ))}
        </div>
      </div>

      {/* Framework scoping */}
      <div>
        <label style={{ fontSize: 11, color: 'var(--text-tertiary)', display: 'block', marginBottom: 6 }}>
          Applies to Frameworks
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <label style={labelStyle}>
            <input
              type="checkbox"
              checked={allFrameworks}
              onChange={() => onFrameworksChange([])}
              style={checkboxStyle}
            />
            All
          </label>
          {FRAMEWORKS.map(fw => (
            <label key={fw} style={labelStyle}>
              <input
                type="checkbox"
                checked={allFrameworks || frameworks.includes(fw)}
                onChange={() => {
                  if (allFrameworks) {
                    onFrameworksChange(FRAMEWORKS.filter(f => f !== fw));
                  } else {
                    toggleFramework(fw);
                  }
                }}
                style={checkboxStyle}
              />
              {fw.replace(/_/g, ' ')}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
